import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { Accident, ReplacementVehicleRequest } from '../types';
import './TrackingMapView.css';

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface TrackingMapViewProps {
  request: ReplacementVehicleRequest;
  accident: Accident;
  vehiclePosition: number; // 0-100 percentage along route
  onVehiclePositionChange?: (position: number) => void;
}

export const TrackingMapView: React.FC<TrackingMapViewProps> = ({
  request,
  accident,
  vehiclePosition,
  onVehiclePositionChange
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const vehicleMarkerRef = useRef<L.Marker | null>(null);
  const routePolylineRef = useRef<L.Polyline | null>(null);
  const progressPolylineRef = useRef<L.Polyline | null>(null);

  // Create route coordinates (simplified path from Mascot to accident location)
  const createRouteCoordinates = (start: L.LatLng, end: L.LatLng): L.LatLng[] => {
    // Create a realistic route through Sydney streets
    const route: L.LatLng[] = [
      start, // Mascot dispatch
      new L.LatLng(-33.9250, 151.1980), // O'Riordan St
      new L.LatLng(-33.9200, 151.2000), // Towards Airport
      new L.LatLng(-33.9100, 151.2050), // Botany Rd
      new L.LatLng(-33.9000, 151.2080), // Towards city
      new L.LatLng(-33.8900, 151.2090), // CBD approach
      new L.LatLng(-33.8800, 151.2095), // City streets
      end // Accident location
    ];
    return route;
  };

  // Interpolate position along route
  const interpolatePosition = (route: L.LatLng[], percentage: number): L.LatLng => {
    if (percentage <= 0) return route[0];
    if (percentage >= 100) return route[route.length - 1];

    const totalDistance = route.reduce((total, point, index) => {
      if (index === 0) return 0;
      return total + point.distanceTo(route[index - 1]);
    }, 0);

    const targetDistance = (percentage / 100) * totalDistance;
    let currentDistance = 0;

    for (let i = 0; i < route.length - 1; i++) {
      const segmentDistance = route[i + 1].distanceTo(route[i]);
      
      if (currentDistance + segmentDistance >= targetDistance) {
        const segmentProgress = (targetDistance - currentDistance) / segmentDistance;
        const lat = route[i].lat + (route[i + 1].lat - route[i].lat) * segmentProgress;
        const lng = route[i].lng + (route[i + 1].lng - route[i].lng) * segmentProgress;
        return new L.LatLng(lat, lng);
      }
      
      currentDistance += segmentDistance;
    }

    return route[route.length - 1];
  };

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create map
    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
      dragging: true,
      scrollWheelZoom: true,
      doubleClickZoom: false,
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '',
      maxZoom: 18,
    }).addTo(map);

    // Create route coordinates
    const startPoint = new L.LatLng(request.dispatchLocation.lat, request.dispatchLocation.lng);
    const endPoint = new L.LatLng(accident.location.lat, accident.location.lng);
    const routeCoordinates = createRouteCoordinates(startPoint, endPoint);

    // Add route polyline (full route in gray)
    const routePolyline = L.polyline(routeCoordinates, {
      color: '#e2e8f0',
      weight: 6,
      opacity: 0.8,
    }).addTo(map);

    // Add progress polyline (completed route in green)
    const progressPolyline = L.polyline([], {
      color: '#10b981',
      weight: 6,
      opacity: 1,
    }).addTo(map);

    // Create custom icons
    const dispatchIcon = L.divIcon({
      className: 'custom-marker dispatch-marker',
      html: `
        <div class="marker-icon dispatch">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
    });

    const destinationIcon = L.divIcon({
      className: 'custom-marker destination-marker',
      html: `
        <div class="marker-icon destination">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="3 11 22 2 13 21 11 13 3 11"/>
          </svg>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
    });

    const vehicleIcon = L.divIcon({
      className: 'custom-marker vehicle-marker',
      html: `
        <div class="marker-icon vehicle">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M7 17a2 2 0 1 0-4 0 2 2 0 0 0 4 0zM21 17a2 2 0 1 0-4 0 2 2 0 0 0 4 0z"/>
            <path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h9l3 6v4a2 2 0 0 1-2 2h-2"/>
            <path d="M9 3h6l3 6"/>
          </svg>
        </div>
      `,
      iconSize: [48, 48],
      iconAnchor: [24, 48],
    });

    // Add dispatch marker
    L.marker(startPoint, { icon: dispatchIcon }).addTo(map)
      .bindPopup(`
        <div class="map-popup">
          <strong>Dispatch Center</strong><br/>
          ${request.dispatchLocation.address}
        </div>
      `);

    // Add destination marker
    L.marker(endPoint, { icon: destinationIcon }).addTo(map)
      .bindPopup(`
        <div class="map-popup">
          <strong>Destination</strong><br/>
          ${accident.title}<br/>
          ${accident.location.address}
        </div>
      `);

    // Add vehicle marker
    const vehicleMarker = L.marker(startPoint, { icon: vehicleIcon }).addTo(map);

    // Fit map to show entire route
    const group = new L.FeatureGroup([routePolyline]);
    map.fitBounds(group.getBounds().pad(0.1));

    // Store references
    mapInstanceRef.current = map;
    vehicleMarkerRef.current = vehicleMarker;
    routePolylineRef.current = routePolyline;
    progressPolylineRef.current = progressPolyline;

    // Store route coordinates for position calculations
    (map as any).routeCoordinates = routeCoordinates;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [request, accident]);

  // Update vehicle position when vehiclePosition changes
  useEffect(() => {
    if (!mapInstanceRef.current || !vehicleMarkerRef.current || !progressPolylineRef.current) return;

    const map = mapInstanceRef.current;
    const routeCoordinates = (map as any).routeCoordinates;
    
    if (!routeCoordinates) return;

    // Calculate new vehicle position
    const newPosition = interpolatePosition(routeCoordinates, vehiclePosition);
    
    // Update vehicle marker position with smooth animation
    vehicleMarkerRef.current.setLatLng(newPosition);

    // Update progress polyline
    const progressCoordinates = [];
    const segmentCount = Math.ceil((vehiclePosition / 100) * routeCoordinates.length);
    
    for (let i = 0; i < Math.min(segmentCount, routeCoordinates.length); i++) {
      progressCoordinates.push(routeCoordinates[i]);
    }
    
    // Add the current vehicle position as the last point
    if (vehiclePosition > 0 && vehiclePosition < 100) {
      progressCoordinates.push(newPosition);
    }

    progressPolylineRef.current.setLatLngs(progressCoordinates);

    // Center map on vehicle if it's moving
    if (vehiclePosition > 10 && vehiclePosition < 90) {
      map.panTo(newPosition, { animate: true, duration: 0.5 });
    }
  }, [vehiclePosition]);

  return (
    <div className="tracking-map-container">
      <div className="tracking-map" ref={mapRef} />
    </div>
  );
}; 